/**
 * WHAT THIS LEARNER HAS ALREADY BEEN SHOWN, per learner and per challenge.
 *
 * Surface brief section 1a: "Every one-shot motion fires from a fact identity,
 * never from mount. ... The last consumed id per learner and challenge is
 * remembered, so a reload, a reconnect, or a route revisit renders the settled
 * state and nothing replays."
 *
 * THE TECHNICAL BRIEF CALLS THIS "the existing ending-state mechanism (the
 * completion-suppression state that already remembers the last ending)". IT
 * DOES NOT EXIST. `useCompletionSuppression` holds the header's numbers while a
 * record is in the viewport and remembers nothing across a mount; no other
 * module in the pinned tree records which ending a learner has seen. So this is
 * that mechanism, built to the shape section 1a specifies and to nothing more:
 * two facts, one ledger, no new served field. It is named in the F1 build
 * report as a module the brief assumed and did not have.
 *
 * IT IS `localStorage` AND NOT `sessionStorage`, and the difference is the
 * whole point. The Vault beat counts from the balance this learner LAST SAW.
 * A ledger that emptied on a tab close would pour the cohort's entire balance
 * into the number again on Monday morning, which is the surface asserting that
 * a fact was just produced when none was. The harness used `sessionStorage`
 * because a design review is one sitting; a run is eight weeks.
 *
 * EVERY READ AND WRITE IS WRAPPED AND THE FALLBACK IS IN MEMORY. A browser that
 * refuses site data (a private window, a locked-down school device, a viewer
 * with storage disabled) must not take the ending down with it. The in-memory
 * ledger keeps the duplicate rule inside one page life and loses it across a
 * reload, which is a weaker guarantee and not a broken one.
 *
 * NOTHING PERSONAL IS WRITTEN HERE. Two integers and a string key: the capsule
 * of the last ending, and the fact ids already consumed. No XP, no balance, no
 * name.
 */

const MEMORY = new Map();

/** The one key shape. Learner and challenge, because a learner has several. */
export const endingKey = (userId, challengeId) =>
  userId && challengeId ? `w4:ending:${userId}:${challengeId}` : null;

/**
 * KEYS WHOSE LAST WRITE THIS BROWSER REFUSED (W4-F2.10 fix batch, MINOR 4).
 *
 * THE FALLBACK USED TO BE ONE-DIRECTIONAL AND THAT WAS THE DEFECT. `write`
 * swallowed a refused `setItem` and `read` preferred any EXISTING stored value
 * over memory, so the common quota case (reads succeed, `setItem` alone fails,
 * a record already present) left two answers: memory holding the new value and
 * storage holding the old one. Every later read took the old one, which is a
 * mirror that says a learner walked through Ship and a ledger that, on the next
 * hydration, says they did not.
 *
 * SO A REFUSED WRITE MAKES MEMORY AUTHORITATIVE FOR THAT KEY, and a later
 * write that lands clears the mark. This is narrower than "memory always wins":
 * a tab whose writes all succeed keeps reading storage, which is what lets it
 * see another tab's write at all (the `storage` listener in `Dashboard.jsx`).
 * A tab that cannot write has no such claim on anyone else's record and answers
 * from the only copy it can keep, which is its own.
 */
const REFUSED = new Set();

const read = (key) => {
  if (!key) return {};
  if (REFUSED.has(key)) return MEMORY.get(key) ?? {};
  try {
    const raw = window.localStorage?.getItem(key);
    if (!raw) return MEMORY.get(key) ?? {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return MEMORY.get(key) ?? {};
  }
};

/**
 * Write, and answer with what the ledger now EFFECTIVELY holds.
 *
 * The return value is the point: a caller that has to mirror this record
 * elsewhere mirrors the effective value rather than the value it hoped for.
 */
const write = (key, value) => {
  if (!key) return value;
  MEMORY.set(key, value);
  try {
    window.localStorage?.setItem(key, JSON.stringify(value));
    REFUSED.delete(key);
  } catch {
    /* The in-memory ledger above is the fallback and is already written, and
       from here on it is the one this key answers from. */
    REFUSED.add(key);
  }
  return value;
};

/**
 * The capsule of the last ending this learner played, or null.
 *
 * NULL IS "NO ENDING YET" AND IT IS NOT THE SAME AS ZERO. The Vault beat reads
 * it to decide which pours are new; a null means every pour so far is new,
 * which is what "the growth since this learner's last ending" says when there
 * has not been one.
 */
export const lastEndingCapsuleId = (userId, challengeId) => {
  const value = read(endingKey(userId, challengeId)).lastCapsuleId;
  return value === undefined || value === null ? null : Number(value);
};

/** Record that this learner has now played the ending for this capsule. */
export const recordEnding = (userId, challengeId, capsuleId) => {
  const key = endingKey(userId, challengeId);
  if (!key || capsuleId === null || capsuleId === undefined) return;
  write(key, { ...read(key), lastCapsuleId: Number(capsuleId) });
};

/**
 * THE BALANCE THIS LEARNER LAST WATCHED THE VAULT BEAT SETTLE ON.
 *
 * THE CAPSULE ALONE WAS NOT ENOUGH, AND TWO SEPARATE DEFECTS SAY SO. The first
 * is a ledger race: the beat's own one-shot motion is marked consumed on its
 * first commit, while the capsule of the last ending was recorded only when the
 * WHOLE sequence settled, so a reload between the two left a beat that had
 * visibly poured and a ledger that had not moved. The second is a capsule that
 * is no longer in the program: the capsule-derived cutoff cannot find it and
 * has to guess, and either guess is wrong for somebody.
 *
 * A NUMBER HAS NEITHER PROBLEM. It is written when the Vault beat itself
 * settles, it is the exact figure the learner was left looking at, and it needs
 * no program topology to interpret. The capsule ledger stays beside it as the
 * fallback for a learner whose last ending predates this field.
 *
 * NOTHING PERSONAL. One integer in the cohort's own currency's minor unit,
 * which is a fact about the cohort's Vault and not about the learner.
 */
export const recordVaultSeen = (userId, challengeId, balanceMinor) => {
  const key = endingKey(userId, challengeId);
  if (!key || balanceMinor === null || balanceMinor === undefined) return;
  const value = Number(balanceMinor);
  if (!Number.isFinite(value)) return;
  write(key, { ...read(key), lastVaultMinor: value });
};

/** The last settled Vault balance, or null where none has been recorded. */
export const lastVaultSeenMinor = (userId, challengeId) => {
  const value = read(endingKey(userId, challengeId)).lastVaultMinor;
  if (value === undefined || value === null) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

/**
 * Has this learner already consumed this fact identity?
 *
 * THE LEDGER IS BOUNDED. A run is eight levels and each level produces at most
 * a handful of ids, but an unbounded list in `localStorage` is an unbounded
 * list in `localStorage`, and the only thing that matters is whether the
 * NEWEST ids are remembered.
 */
export const CONSUMED_CAP = 32;

export const hasConsumed = (userId, challengeId, factId) => {
  if (!factId) return false;
  const consumed = read(endingKey(userId, challengeId)).consumed;
  return Array.isArray(consumed) && consumed.includes(factId);
};

export const consume = (userId, challengeId, factId) => {
  const key = endingKey(userId, challengeId);
  if (!key || !factId) return;
  const state = read(key);
  const consumed = Array.isArray(state.consumed) ? state.consumed : [];
  if (consumed.includes(factId)) return;
  write(key, {
    ...state,
    consumed: [...consumed, factId].slice(-CONSUMED_CAP),
  });
};

/* ------------------------------------------------------------------ *
 * The Vault reveal acknowledgement, held above the composition.
 * ------------------------------------------------------------------ */

/**
 * WHICH VAULT FACTS THIS PAGE LIFE HAS ALREADY ACKNOWLEDGED (W4-F2.9 fix
 * batch, ruling 4).
 *
 * THE GUARD USED TO BE A REF INSIDE `VaultComposition`, AND A REF DIES WITH
 * ITS COMPONENT. Remount the same ending and the fresh component asks the
 * consumed-id ledger whether to play, is told no, settles in its first commit
 * and acknowledges a second time for a fact the learner watched once. The
 * cross review measured that path; the server treating a stale `as_of` as a
 * no-op is a reason it is not urgent, not a reason to send it.
 *
 * SO THE GUARD LIVES ABOVE THE COMPOSITION, KEYED BY THE LEARNER, THE
 * CHALLENGE AND THE FROZEN FACT. `vaultFactId` is `vault:capsule:balance`, so
 * that key already names the ending and the fact together: a different level,
 * or a later pour, is a different key and acknowledges on its own.
 *
 * IT IS IN MEMORY AND IT IS NOT WRITTEN TO `localStorage`, WHICH IS THE
 * DELIBERATE HALF OF THIS. An acknowledgement whose POST never landed must be
 * retryable, and D9 retries it by replaying the ending on the learner's next
 * visit. A durable guard would suppress that retry for ever and leave the
 * reveal point behind the balance the learner has already been shown.
 *
 * NOTHING PERSONAL. Ids and a balance, the same two facts the key beside it
 * already carries.
 */
const ACKED_VAULT_FACTS = new Set();

const ackKey = (userId, challengeId, factId) =>
  factId ? `${userId ?? "-"}:${challengeId ?? "-"}:${factId}` : null;

export const hasAckedVaultReveal = (userId, challengeId, factId) => {
  const key = ackKey(userId, challengeId, factId);
  return key !== null && ACKED_VAULT_FACTS.has(key);
};

export const markVaultRevealAcked = (userId, challengeId, factId) => {
  const key = ackKey(userId, challengeId, factId);
  if (key !== null) ACKED_VAULT_FACTS.add(key);
};

/** Test seam: the ledger is process state, so a suite has to be able to clear it. */
export const __clearEndingState = () => {
  MEMORY.clear();
  ACKED_VAULT_FACTS.clear();
  REFUSED.clear();
  try {
    Object.keys(window.localStorage ?? {})
      .filter((key) => key.startsWith("w4:ending:"))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch {
    /* nothing to clear */
  }
};

/* ------------------------------------------------------------------ *
 * The submission boundary's own memory (W4-F1, technical brief 5.3 and 7).
 * ------------------------------------------------------------------ */

/**
 * AN UPLOAD WHOSE FLOOR RESULT HAS NOT LANDED YET.
 *
 * On a `scored` level in a versioned cohort the ship event and the ending wait
 * for `floor_status` to become `passed` or `exempt`. Usually that is a few
 * seconds and the `floor` socket message releases it in the same page life. It
 * is NOT always: the budget is twenty seconds, the reader retries at five and
 * fifteen minutes, and a teacher override can land the next day. Ruling S7 rule
 * 7 says the ending then plays on the learner's next visit.
 *
 * SO THE PARK IS DURABLE, AND THAT IS WHAT MAKES THE NEXT-VISIT PATH SAFE.
 * The alternative reading of D9 is to play an ending for any passed output the
 * ledger has no ending recorded for, and that would fire a full-screen ending
 * for the current level on any device whose storage was cleared, for a learner
 * who saw it a week ago. This records the one thing that is actually true: THIS
 * learner, on THIS device, uploaded and is waiting. No park, no ending.
 *
 * NOTHING PERSONAL IS STORED. Three fields, all of them facts about the level
 * rather than about the learner: which capsule, which level number, and whether
 * it is the last one.
 */
/**
 * ONE ENTRY PER CAPSULE, NOT ONE SLOT.
 *
 * A single slot loses an ending the moment a learner has two parks out at once,
 * and that is not exotic: two levels shipped in one sitting while the first is
 * still waiting on its reader, or a park the release refused to consume because
 * another ending was already on screen. The second park overwrote the first and
 * the first ending was gone for good.
 */
const PENDING_SHIPS = "pendingShips";

/**
 * WHICH TAB OWNS A PARK, AND FOR HOW LONG.
 *
 * Two tabs on the same learner share one `localStorage` record and each has its
 * own store, so both can reach the release for the same park in the same
 * second, and one tab's clear can delete the record the other still needs. The
 * claim is a tab-scoped string with a timestamp: a tab may consume a park it
 * owns, or one whose claim has gone stale, and nothing else. It is a lock in
 * one browser rather than a distributed one, which is the whole of the problem.
 */
const CLAIM_TTL_MS = 15000;

const TAB_ID = `t${Math.random().toString(36).slice(2)}${Date.now()}`;

/** Test seam: the tab identity, so a suite can play the second tab. */
export const __tabId = () => TAB_ID;

const now = () => Date.now();

const claimIsFree = (claim, tabId) => {
  if (!claim || !claim.tabId) return true;
  if (claim.tabId === tabId) return true;
  return now() - Number(claim.at ?? 0) > CLAIM_TTL_MS;
};

const readPending = (key) => {
  const state = read(key);
  const held = state[PENDING_SHIPS];
  if (held && typeof held === "object") return held;
  /* A record written before this field existed carries one slot. It is read
     once, keyed by its own capsule, so an upgrade in mid-run loses no park. */
  const legacy = state.pendingShip;
  if (legacy && legacy.capsuleId !== undefined && legacy.capsuleId !== null) {
    return { [String(Number(legacy.capsuleId))]: legacy };
  }
  return {};
};

export const recordPendingShip = (userId, challengeId, pending) => {
  const key = endingKey(userId, challengeId);
  if (!key || !pending || pending.capsuleId === null) return;
  const state = read(key);
  const next = { ...state, [PENDING_SHIPS]: { ...readPending(key) } };
  next[PENDING_SHIPS][String(Number(pending.capsuleId))] = {
    capsuleId: Number(pending.capsuleId),
    level: pending.level ?? null,
    isLastLevel: pending.isLastLevel === true,
    claim: { tabId: TAB_ID, at: now() },
  };
  delete next.pendingShip;
  write(key, next);
};

/** Every park this learner has out for this challenge, newest first by nothing. */
export const pendingShips = (userId, challengeId) =>
  Object.values(readPending(endingKey(userId, challengeId)));

/**
 * The park this tab may act on, or null.
 *
 * `capsuleId` narrows it to one park; without it the first park this tab may
 * claim is returned, which is what the next-visit path asks for.
 */
export const pendingShip = (userId, challengeId, capsuleId = null) => {
  const held = readPending(endingKey(userId, challengeId));
  const rows =
    capsuleId === null || capsuleId === undefined
      ? Object.values(held)
      : [held[String(Number(capsuleId))]].filter(Boolean);
  return rows.find((row) => claimIsFree(row?.claim, TAB_ID)) ?? null;
};

export const clearPendingShip = (userId, challengeId, capsuleId = null) => {
  const key = endingKey(userId, challengeId);
  if (!key) return;
  const state = read(key);
  const held = readPending(key);
  const next = { ...state, [PENDING_SHIPS]: { ...held } };
  if (capsuleId === null || capsuleId === undefined) {
    next[PENDING_SHIPS] = {};
  } else {
    delete next[PENDING_SHIPS][String(Number(capsuleId))];
  }
  delete next.pendingShip;
  write(key, next);
};

/* ------------------------------------------------------------------ *
 * The ship walkthrough (W4-F2.10, Addendum 5 rule 4).
 * ------------------------------------------------------------------ */

/**
 * WHICH LEVELS THIS LEARNER HAS PERSONALLY WALKED THROUGH SHIP.
 *
 * Addendum 5 rule 1: the squad's output being in is a fact about the squad and
 * it never moves a learner's rail on its own. Wave 4 B2's reader floor stamps
 * `capsule_users.deliverables_submitted = 1` for every member of the roster
 * snapshot the moment the output passes (`FloorTransition.php:55-60`), so that
 * column stopped being able to answer "has THIS learner walked through Ship".
 * Rule 4 names the signal that can: the learner's own ship event, recorded the
 * moment it fires, for the uploader and the already-submitted learner alike.
 *
 * IT LIVES HERE AND NOT IN A SECOND LEDGER. This module already is the record
 * of what a learner has been shown, under the same `(learner, challenge)` key,
 * with the same wrapped reads, the same in-memory fallback and the same
 * `__clearEndingState` seam. A parallel store keyed the same way would be a
 * second answer to one question.
 *
 * IT IS A SET AND NOT `lastCapsuleId`. The ending ledger's single slot answers
 * "which ending played last", which is what the Vault beat's cutoff needs. This
 * question is asked PER CAPSULE, on every render of the cascade, about a capsule
 * that is not necessarily the most recent one: a learner can hold a park on
 * level 2 while the rail is deciding level 3, and capsule ids are not ordered by
 * anything this module may rely on. A slot would answer for one level and guess
 * at every other.
 *
 * NOTHING PERSONAL IS WRITTEN. Capsule ids under a key that already carries the
 * learner and the challenge, which is the same two facts the fields above hold.
 *
 * THE DISCLOSED COST IS THE RULING'S, NOT A DEFECT (Addendum 5, "Disclosed
 * cost"). This is client state, so on a second device or a cleared browser a
 * learner past Ship on a level is offered Ship once more and the ending plays
 * once more for them there. That is the same class of cost D9 already accepts
 * for the Vault ledger beside it. The durable fix is a server-side per-learner
 * acknowledgment written on the ship event, queued as W4-B2.12.
 */
const SHIP_WALKTHROUGHS = "shipWalkthroughs";

/**
 * A RUN IS EIGHT LEVELS, SO THE LIST IS BOUNDED AT THE SAME CAP THE CONSUMED
 * IDS USE. An unbounded list in `localStorage` is an unbounded list in
 * `localStorage`, and only the newest entries can matter: a capsule that fell
 * off the end is a level the server stopped reporting as the learner's own
 * position long ago.
 */
export const SHIP_WALKTHROUGH_CAP = 32;

const readWalkthroughs = (key) => {
  const held = read(key)[SHIP_WALKTHROUGHS];
  if (!Array.isArray(held)) return [];
  return held
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));
};

/** Every capsule this learner has walked through Ship on, for this challenge. */
export const shipWalkthroughs = (userId, challengeId) =>
  readWalkthroughs(endingKey(userId, challengeId));

/** Has this learner walked through Ship on this capsule? */
export const hasShipWalkthrough = (userId, challengeId, capsuleId) => {
  if (capsuleId === null || capsuleId === undefined) return false;
  const target = Number(capsuleId);
  if (!Number.isFinite(target)) return false;
  return shipWalkthroughs(userId, challengeId).includes(target);
};

/**
 * Record that this learner has now walked through Ship on this capsule, and
 * answer with the list the ledger EFFECTIVELY holds afterwards.
 *
 * IDEMPOTENT, because the boundary can be reached twice for one level: a
 * `try again` after a refused floor, a park released on a later visit, a
 * duplicate event the sequence's own second-event branch already warns about.
 *
 * THE RETURN VALUE IS THE FIX FOR MINOR 4. The store mirror used to append the
 * capsule it ASKED for, which on a browser that refused the write was a claim
 * the ledger could not reproduce. It now mirrors this answer, which is read
 * back through `readWalkthroughs` after the write and is therefore whatever
 * this browser will hand the next hydration: the stored list where the write
 * landed, the in-memory list where it did not.
 */
export const recordShipWalkthrough = (userId, challengeId, capsuleId) => {
  const key = endingKey(userId, challengeId);
  if (!key) return [];
  const held = readWalkthroughs(key);
  if (capsuleId === null || capsuleId === undefined) return held;
  const target = Number(capsuleId);
  if (!Number.isFinite(target)) return held;
  if (held.includes(target)) return held;
  write(key, {
    ...read(key),
    [SHIP_WALKTHROUGHS]: [...held, target].slice(-SHIP_WALKTHROUGH_CAP),
  });
  return readWalkthroughs(key);
};

/* ------------------------------------------------------------------ *
 * The squad's stamp carried this learner past Plan and Work.
 * ------------------------------------------------------------------ */

/**
 * WHICH LEVEL THIS LEARNER WAS CARRIED FORWARD ON, RECORDED WHEN IT HAPPENS
 * (W4-F2.11 fix batch, Codex MINOR 1).
 *
 * WHAT IT REPLACES AND WHY THAT HAD TO GO. The moved-forward line first asked
 * `deliverablesSubmitted && !pastOwnShip && !hasOwnTask`, which reconstructs a
 * past event from the CURRENT quest collection. A learner who claimed a task,
 * finished it and walked to Check-in, and whose quest was then deleted or moved
 * to another capsule before the squad's output was stamped, loses `hasOwnTask`
 * and is told the squad moved them forward. It is a claim about history made
 * from a fact about the present, and the present can change underneath it.
 *
 * SO THE TRANSITION IS RECORDED AT THE TRANSITION. `deriveJourneyStep`'s
 * write-through shortcut is the one place the carry actually happens (the
 * branch that dispatches `SET_TASK_CREATED` because the output is in and the
 * learner has no task), and this is the mark it leaves. Everything downstream
 * reads the mark.
 *
 * ONE SLOT AND NOT A LIST, WHICH IS ALSO HOW IT IS CLEARED ON A CAPSULE CHANGE.
 * The line is only ever rendered for the level the learner is standing on, and
 * a learner stands on one. A second carry on a later level replaces the first,
 * and a mark naming another capsule answers false for this one, so "clear it on
 * capsule change" comes out of the shape rather than out of a listener.
 *
 * NOTHING PERSONAL IS WRITTEN. One capsule id, under the key that already
 * carries the learner and the challenge.
 */
const MOVED_FORWARD = "movedForwardCapsuleId";

/** The capsule this learner was carried forward on, or null. */
export const movedForwardCapsuleId = (userId, challengeId) => {
  const value = read(endingKey(userId, challengeId))[MOVED_FORWARD];
  if (value === undefined || value === null) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

/**
 * Record the carry, and answer with what the ledger EFFECTIVELY holds.
 *
 * The return value is the point, exactly as it is for the walkthrough beside
 * it: a caller that has to mirror this into the store mirrors the value this
 * browser will hand the next hydration rather than the value it hoped for.
 */
export const recordMovedForward = (userId, challengeId, capsuleId) => {
  const key = endingKey(userId, challengeId);
  if (!key) return null;
  if (capsuleId === null || capsuleId === undefined)
    return movedForwardCapsuleId(userId, challengeId);
  const target = Number(capsuleId);
  if (!Number.isFinite(target))
    return movedForwardCapsuleId(userId, challengeId);
  write(key, { ...read(key), [MOVED_FORWARD]: target });
  return movedForwardCapsuleId(userId, challengeId);
};

/**
 * Clear the carry once the learner has walked through Ship on that level.
 *
 * The line is a Check-in line and a learner past Ship is past Check-in, so this
 * changes nothing the learner can see. It is here because a mark with no
 * expiry is a mark that outlives what it describes, and the level the learner
 * closed is the moment it stops being true of anything.
 *
 * IT CLEARS ONLY THE NAMED CAPSULE'S MARK. A walkthrough on level 2 must not
 * erase a carry recorded on level 3 by a page that has already moved on.
 */
export const clearMovedForward = (userId, challengeId, capsuleId) => {
  const key = endingKey(userId, challengeId);
  if (!key) return null;
  const held = movedForwardCapsuleId(userId, challengeId);
  if (held === null) return null;
  if (capsuleId !== null && capsuleId !== undefined) {
    const target = Number(capsuleId);
    if (!Number.isFinite(target) || target !== held) return held;
  }
  const state = { ...read(key) };
  delete state[MOVED_FORWARD];
  write(key, state);
  return movedForwardCapsuleId(userId, challengeId);
};
